import { MenuIcon, SearchIcon, UserRoundIcon } from "lucide-react";
import * as React from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { checkContract, inDemo } from "@/api/client";
import { useBilling, useLeads, useWorkedCounties } from "@/api/queries";
import { CountySwitcher } from "@/components/counties";
import { workedLabel } from "@/lib/counties";
import { Logo } from "@/components/logo";
import { StartPlan } from "@/components/start-plan";
import { useAccount, useSignOut } from "@/lib/session";
import { TwoStepRequired } from "@/components/two-step";
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { leaveDemo } from "@/lib/demo";
import { DEFAULT_QUERY } from "@/lib/leads-query";
import { cn } from "@/lib/utils";

/** The main navigation holds exactly the screens the spec names. */
const NAV = [
  { to: "/today", label: "Today" },
  { to: "/estates", label: "Estates" },
  { to: "/leads", label: "Leads" },
  { to: "/mail-run", label: "Mail run" },
  { to: "/calls", label: "Call session" },
] as const;

/** An admin opens every lead, so Estates, which lists what is still locked, is empty for them. */
function useNav() {
  const { role } = useAccount();
  return NAV.filter((item) => item.to !== "/estates" || role !== "admin");
}

const navLink =
  "inline-flex min-h-[max(var(--control-h),var(--touch-min))] items-center whitespace-nowrap rounded-control px-3 font-sans text-sm font-medium text-heading no-underline transition-[background-color] duration-(--dur-fast) hover:bg-surface-active aria-[current=page]:bg-surface-active aria-[current=page]:font-semibold";

function Wordmark() {
  return (
    <NavLink to="/today" className="flex items-center text-heading no-underline" aria-label="EstateLark, Today">
      <Logo className="h-8 w-auto" />
    </NavLink>
  );
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

/** Stops the app before a page can break on a server older than this build. */
function StaleServerNotice({ missing }: { missing: string[] }) {
  return (
    <main className="mx-auto max-w-xl p-6" role="alert">
      <h1 className="font-display text-2xl text-heading">The workspace server is older than this app</h1>
      <p className="mt-2 text-sm text-muted">
        Restart it from the repo folder with <code className="font-mono">.venv/bin/deedbeacon workspace</code> and reload this page.
      </p>
      <p className="mt-2 text-xs text-muted">Missing: {missing.join(", ")}</p>
    </main>
  );
}

/** Find a lead by petitioner, deceased or case number, from anywhere (Ctrl or Cmd + K). */
function LeadFinder() {
  const [open, setOpen] = React.useState(false);
  const [text, setText] = React.useState("");
  const q = useDebounced(text.trim(), 150);
  const navigate = useNavigate();
  const location = useLocation();
  const results = useLeads({ ...DEFAULT_QUERY, q }, open && Boolean(q));

  React.useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === "k" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setOpen((value) => !value);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const rows = q ? (results.data?.rows ?? []).slice(0, 8) : [];
  return (
    <>
      <Button variant="outline" className="gap-2 text-muted max-md:hidden" onClick={() => setOpen(true)} id="find-lead">
        <SearchIcon aria-hidden />
        <span>Find a lead</span>
        <kbd className="ms-2 inline-flex min-w-6 items-center justify-center rounded-xs border border-border-strong bg-surface-raised px-1.5 py-0.5 font-mono text-xs font-medium text-muted shadow-(--elevation-1)">
          ⌘K
        </kbd>
      </Button>
      <Button variant="ghost" size="icon" className="md:hidden" aria-label="Find a lead" onClick={() => setOpen(true)}>
        <SearchIcon aria-hidden />
      </Button>
      <CommandDialog
        open={open}
        onOpenChange={(value) => {
          setOpen(value);
          if (!value) setText("");
        }}
        title="Find a lead"
        description="Search by petitioner, deceased or case number."
        shouldFilter={false}
      >
        <CommandInput placeholder="Petitioner, deceased or case number" value={text} onValueChange={setText} />
        <CommandList>
          {q && !results.isFetching && rows.length === 0 ? <CommandEmpty>No lead matches.</CommandEmpty> : null}
          {!q ? <p className="m-0 px-3 py-6 text-center text-sm text-muted">Type a name or a case number.</p> : null}
          {rows.length ? (
            <CommandGroup heading="Leads">
              {rows.map((lead) => (
                <CommandItem
                  key={lead.lead_id}
                  value={lead.lead_id}
                  onSelect={() => {
                    setOpen(false);
                    setText("");
                    void navigate(`/leads/${encodeURIComponent(lead.lead_id)}`, { state: { from: location.pathname + location.search } });
                  }}
                >
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate font-semibold">{lead.petitioner_name || "Not named"}</span>
                    <span className="truncate text-xs text-muted">Estate of {lead.deceased_name}</span>
                  </span>
                  <span className="ms-auto font-mono text-xs text-muted">{lead.case_number}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          ) : null}
        </CommandList>
      </CommandDialog>
    </>
  );
}

function MobileNav() {
  const nav = useNav();
  const [open, setOpen] = React.useState(false);
  const { worked } = useWorkedCounties();
  const signOut = useSignOut();
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden" aria-label="Open the menu">
          <MenuIcon aria-hidden />
        </Button>
      </SheetTrigger>
      <SheetContent side="left">
        <SheetHeader>
          <SheetTitle>EstateLark</SheetTitle>
          <SheetDescription>Probate leads</SheetDescription>
        </SheetHeader>
        <nav aria-label="Main" className="flex flex-col gap-1 px-3">
          {nav.map((item) => (
            <NavLink key={item.to} to={item.to} className={cn(navLink, "w-full")} onClick={() => setOpen(false)}>
              {item.label}
            </NavLink>
          ))}
          <NavLink to="/counties" className={cn(navLink, "w-full justify-between")} onClick={() => setOpen(false)} id="mobile-counties">
            <span>Counties</span>
            {worked.length ? <span className="text-xs font-normal text-muted">{workedLabel(worked)}</span> : null}
          </NavLink>
          <NavLink to="/settings" className={cn(navLink, "w-full text-muted")} onClick={() => setOpen(false)}>
            Data and settings
          </NavLink>
          <NavLink to="/account" className={cn(navLink, "w-full text-muted")} onClick={() => setOpen(false)} id="mobile-account">
            Your account
          </NavLink>
          <button type="button" className={cn(navLink, "w-full cursor-pointer border-0 bg-transparent text-muted")} onClick={() => signOut.mutate()}>
            Sign out
          </button>
        </nav>
      </SheetContent>
    </Sheet>
  );
}

/** The signed-in account, one click from every screen; on a phone it lives in the menu, which an
 * account with no county does not have, so then it shows there too. */
function AccountLink({ signedUpOnly = false }: { signedUpOnly?: boolean }) {
  const account = useAccount();
  return (
    <Button variant="ghost" size="icon" asChild className={signedUpOnly ? undefined : "max-md:hidden"}>
      <NavLink to="/account" aria-label={`Your account, ${account.email}`} id="account-link" title={account.email}>
        <UserRoundIcon aria-hidden />
      </NavLink>
    </Button>
  );
}

/** A renewal that failed while Stripe retries: the counties stay open, and the card needs changing. */
function PaymentNotice() {
  const billing = useBilling();
  if (!billing.data?.payment_failed) return null;
  return (
    <aside aria-label="Payment notice" id="payment-notice">
      <p className="m-0 border-b border-border bg-warning-tint px-4 py-2 text-center text-sm text-warning-ink">
        Your last payment didn't go through. You keep your counties while Stripe tries again.{" "}
        <NavLink to="/account" className="font-semibold underline underline-offset-2">
          Update your card
        </NavLink>
      </p>
    </aside>
  );
}

/** The admin of a server that requires two-step sign-in sees only its setup until it is on;
 * every other route answers 403 meanwhile, so no screen behind the menu would load. */
export function AppShell() {
  const account = useAccount();
  if (account.two_step_required) {
    return (
      <div className="flex min-h-dvh flex-col bg-background text-foreground">
        <header className="border-b border-border bg-surface-raised">
          <div className="mx-auto flex h-16 w-full max-w-[80rem] items-center gap-4 px-12 max-lg:px-6 max-sm:px-4">
            <Wordmark />
          </div>
        </header>
        <main id="main" className="mx-auto flex w-full max-w-[80rem] flex-1 flex-col gap-6 px-12 pb-12 pt-8 max-lg:px-6 max-sm:px-4 max-sm:pt-6">
          <TwoStepRequired />
        </main>
      </div>
    );
  }
  return <WorkingShell />;
}

function WorkingShell() {
  const nav = useNav();
  const account = useAccount();
  const contract = useQuery({ queryKey: ["contract"], queryFn: checkContract, staleTime: Infinity, retry: false });
  const { all, worked, isLoading } = useWorkedCounties();
  const location = useLocation();
  const accountPage = location.pathname === "/account" || location.pathname === "/accounts";
  // An account with no county yet has nothing behind the menu, so it shows only the way to start.
  const subscribed = !isLoading && all.length > 0;
  const mock = import.meta.env.MODE === "mock";
  return (
    <div className="flex min-h-dvh flex-col bg-background text-foreground">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-2 focus:z-(--z-toast) focus:rounded-sm focus:bg-surface-raised focus:px-3 focus:py-2">
        Skip to the page
      </a>
      {inDemo() ? (
        <aside aria-label="Sample workspace" id="sample-notice">
          <p className="m-0 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b border-border bg-surface-soft px-4 py-2 text-center text-sm text-heading">
            <span>
              <span className="font-semibold">Sample workspace.</span> Every family here is invented, and nothing you do leaves this browser.
              <span className="max-sm:hidden"> Open an estate, log a call, and see the follow-up it schedules.</span>
            </span>
            <Button size="sm" variant="outline" onClick={leaveDemo} id="leave-sample">
              Leave the sample
            </Button>
          </p>
        </aside>
      ) : (
        <aside aria-label="Prototype notice">
          <p className="m-0 border-b border-border bg-surface-soft px-4 py-1.5 text-center text-xs text-heading">
            Prototype. Nothing is sent from here.
            {mock ? <span className="font-semibold"> Mock data: every lead on these pages is invented.</span> : null}
          </p>
        </aside>
      )}
      {subscribed && account.role !== "admin" ? <PaymentNotice /> : null}
      <header className="sticky top-0 z-(--z-sticky) border-b border-border bg-surface-raised">
        <div className="mx-auto flex h-16 w-full max-w-[80rem] items-center gap-4 px-12 max-lg:px-6 max-sm:px-4">
          {subscribed ? <MobileNav /> : null}
          <Wordmark />
          {subscribed ? (
            <nav aria-label="Main" className="ms-6 flex items-center gap-1 max-md:hidden">
              {nav.map((item) => (
                <NavLink key={item.to} to={item.to} className={navLink}>
                  {item.label}
                </NavLink>
              ))}
            </nav>
          ) : null}
          <div className="ms-auto flex items-center gap-2">
            {subscribed ? (
              <>
                <CountySwitcher />
                <LeadFinder />
              </>
            ) : null}
            <AccountLink signedUpOnly={!subscribed} />
          </div>
        </div>
      </header>
      <main id="main" className="mx-auto flex w-full max-w-[80rem] flex-1 flex-col gap-6 px-12 pb-12 pt-8 max-lg:px-6 max-sm:px-4 max-sm:pt-6">
        {contract.data && contract.data.length > 0 ? (
          <StaleServerNotice missing={contract.data} />
        ) : !isLoading && all.length === 0 && !accountPage ? (
          <StartPlan />
        ) : (
          <Outlet />
        )}
      </main>
      <footer className="border-t border-border">
        <div className="mx-auto flex w-full max-w-[80rem] items-center justify-between gap-4 px-12 py-4 text-sm text-muted max-lg:px-6 max-sm:px-4">
          {worked.length > 0 ? (
            <NavLink to="/counties" id="counties" className="text-muted no-underline underline-offset-2 hover:underline">
              {worked.map((county) => county.display).join(" · ")}
            </NavLink>
          ) : (
            <span />
          )}
          {subscribed ? (
            <NavLink to="/settings" className="text-link underline-offset-2 hover:underline aria-[current=page]:font-semibold" id="settings-link">
              Data and settings
            </NavLink>
          ) : null}
        </div>
        {Array.from(new Set(worked.map((county) => county.source_notice).filter(Boolean))).map((notice) => (
          <p key={notice} className="mx-auto m-0 w-full max-w-[80rem] px-12 pb-4 text-xs leading-relaxed text-muted max-lg:px-6 max-sm:px-4" data-testid="source-notice">{notice}</p>
        ))}
      </footer>
    </div>
  );
}
